import type { TransactionListDto } from "../../api/types";
import type { RowEdit } from "./editSession";
import { sameMoney } from "../../logic/money";

/// Fields where the server's preview of the pending actions differs from the
/// stored row. Iterates the list row's keys, so indexing the preview (a
/// superset shape) is sound. Skipped keys never take part in edit diffs:
/// identity, and projection-only fields the preview (a full `TransactionDto`)
/// reports differently or not at all — `hasMatchProblem` and `derivedAccount`
/// are list-only, the preview reports fall-throughs as its own `problems`
/// list instead.
const previewDiff = (
  tx: TransactionListDto,
  edit: RowEdit | undefined,
): Partial<TransactionListDto> => {
  const diff: Partial<TransactionListDto> = {};
  if (!edit?.preview) return diff;
  for (const k in tx) {
    const key = k as keyof TransactionListDto;
    if (
      key === "id" ||
      key === "appliedRules" ||
      key === "matchingRules" ||
      key === "hasMatchProblem" ||
      key === "derivedAccount"
    ) {
      continue;
    }
    const a = edit.preview[key];
    const b = tx[key];
    const equal =
      key === "amount" || key === "foreignAmount"
        ? sameMoney(edit.preview[key], tx[key])
        : Array.isArray(a) && Array.isArray(b)
          ? a.length === b.length && a.every((v, i) => v === b[i])
          : a === b;
    if (!equal) {
      // @ts-expect-error Types are safe but TS can't infer key mapping
      diff[key] = a;
    }
  }
  return diff;
};

/// The row as the pending edits have left it. Overlay wins over preview: a
/// preview is only stored while it answers for the current actions, so where
/// the two disagree the overlay is the newer edit.
export const effectiveRow = (
  tx: TransactionListDto,
  edit: RowEdit | undefined,
): TransactionListDto => ({
  ...tx,
  ...previewDiff(tx, edit),
  ...edit?.overlay,
});

export type RowView = {
  /// Field values to display.
  values: TransactionListDto;
  /// Fields whose displayed value is a pending edit (highlighted in the UI).
  pending: ReadonlySet<keyof TransactionListDto>;
};

export const deriveRowView = (
  tx: TransactionListDto,
  edit: RowEdit | undefined,
): RowView => {
  const diff = { ...previewDiff(tx, edit), ...edit?.overlay };
  return {
    values: { ...tx, ...diff },
    pending: new Set(Object.keys(diff) as (keyof TransactionListDto)[]),
  };
};
